import { useState } from 'react'
import { useMembers } from '../../../hooks/useMembers'
import { useAuth } from '../../../hooks/useAuth'
import { useContactPhotoUrl } from '../hooks/useContacts'
import { cn, getInitials } from '../../../lib/utils'
import { Spinner } from '../../../components/ui/Spinner'
import { Portal } from '../../../components/Portal'

const RelBadge = ({ rel }) => (
  <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-primary-light text-primary capitalize">
    {rel}
  </span>
)

const InfoRow = ({ icon, label, value, href }) => {
  if (!value) return null
  const content = (
    <div className="flex items-start gap-3 py-2.5">
      <div className="text-text-muted mt-0.5 flex-shrink-0">{icon}</div>
      <div>
        <div className="text-xs text-text-muted mb-0.5">{label}</div>
        <div className="text-sm text-text-primary">{value}</div>
      </div>
    </div>
  )
  if (href) return <a href={href} className="block hover:bg-background rounded-lg px-1 -mx-1">{content}</a>
  return <div className="px-1 -mx-1">{content}</div>
}

const PhoneIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
  </svg>
)

const EmailIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
  </svg>
)

const AddressIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
  </svg>
)

const OrgIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
  </svg>
)

export const ContactDetail = ({ contact, onEdit, onDelete, onClose }) => {
  const { isAdmin, isLimited } = useAuth()
  const { data: members = [] }  = useMembers()
  const { data: photoUrl }      = useContactPhotoUrl(contact.photo_path)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting,      setDeleting]      = useState(false)

  const fullName = [contact.first_name, contact.last_name].filter(Boolean).join(' ')

  const memberMap = {}
  for (const m of members) {
    if (m.profiles) memberMap[m.user_id] = m.profiles
  }

  const assocMembers = (contact.member_ids ?? [])
    .map((uid) => memberMap[uid])
    .filter(Boolean)

  const canEdit = !isLimited

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await onDelete({ id: contact.id, photoPath: contact.photo_path })
      onClose()
    } catch {
      setDeleting(false)
    }
  }

  return (
    <Portal>
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-0 overflow-hidden" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90dvh] flex flex-col shadow-card"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-primary-light flex items-center justify-center overflow-hidden flex-shrink-0">
              {photoUrl ? (
                <img src={photoUrl} alt={fullName} className="w-full h-full object-cover" />
              ) : (
                <span className="text-primary font-bold text-base">{getInitials(fullName)}</span>
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold text-text-primary">{fullName}</h2>
                {contact.is_emergency && (
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-rose-100 text-rose-600">Emergency</span>
                )}
              </div>
              <RelBadge rel={contact.relationship} />
            </div>
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary p-1 flex-shrink-0">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 min-h-0 px-5 py-4 space-y-4">
          {/* Contact info */}
          <div className="space-y-0.5">
            <InfoRow icon={<OrgIcon />}     label="Organization"    value={contact.organization} />
            <InfoRow icon={<PhoneIcon />}   label="Primary phone"   value={contact.phone_primary}
              href={contact.phone_primary ? `tel:${contact.phone_primary}` : null} />
            <InfoRow icon={<PhoneIcon />}   label="Secondary phone" value={contact.phone_secondary}
              href={contact.phone_secondary ? `tel:${contact.phone_secondary}` : null} />
            <InfoRow icon={<EmailIcon />}   label="Email"           value={contact.email}
              href={contact.email ? `mailto:${contact.email}` : null} />
            <InfoRow icon={<AddressIcon />} label="Address"         value={contact.address} />
          </div>

          {/* Associated members */}
          {assocMembers.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">Associated Members</div>
              <div className="flex flex-wrap gap-2">
                {assocMembers.map((p) => (
                  <span key={p.id}
                    className="text-xs font-medium px-2.5 py-1 rounded-full text-white"
                    style={{ backgroundColor: p.member_color ?? '#7c6faf' }}>
                    {p.display_name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {contact.notes && (
            <div>
              <div className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">Notes</div>
              <p className="text-sm text-text-secondary whitespace-pre-wrap">{contact.notes}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        {canEdit && (
          <div className="px-5 py-4 border-t border-border flex gap-2 flex-shrink-0">
            {confirmDelete ? (
              <>
                <button onClick={() => setConfirmDelete(false)}
                  className="flex-1 border border-border rounded-xl py-2.5 text-sm font-medium text-text-secondary">
                  Cancel
                </button>
                <button onClick={handleDelete} disabled={deleting}
                  className="flex-1 bg-rose-500 text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
                  {deleting ? <Spinner size="sm" /> : 'Confirm Delete'}
                </button>
              </>
            ) : (
              <>
                <button onClick={() => setConfirmDelete(true)}
                  className="flex-1 bg-rose-500 text-white rounded-xl py-2.5 text-sm font-semibold">
                  Delete
                </button>
                <button onClick={() => onEdit(contact)}
                  className="flex-1 border border-border rounded-xl py-2.5 text-sm font-medium text-text-secondary">
                  Edit
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
    </Portal>
  )
}
